import React from 'react';
import { Printer, Download, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import {
  type PrintPreviewTemplate,
  type DocumentConfig,
  type DocumentFormState,
} from '../documentModalHelpers';
import { type Document, type DocumentLineItem } from '@/hooks/useDocumentsWorkItems';
import { authService } from '@/lib/authService';
import { formatDateSAST } from '@/lib/sastTime';
import type { Contact } from '@/app/client-zone/modules/total-finance/hooks/useContacts';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';

interface ClientBranding {
  primary_color?: string;
  secondary_color?: string;
  header_color?: string;
  primaryColor?: string;
  secondaryColor?: string;
  headerColor?: string;
  logo_url?: string;
  trading_name?: string;
  favicon_url?: string;
}

interface ClientDetails {
  trading_name?: string;
  registered_name?: string;
  company_registration_number?: string;
  vat_number?: string;
  physical_address_line1?: string;
  physical_address_line2?: string;
  physical_address_city?: string;
  physical_address_province?: string;
  physical_address_postal_code?: string;
  phone?: string;
  email?: string;
  bank_name?: string;
  bank_account_number?: string;
  bank_branch_code?: string;
  bank_account_type?: string;
  logo_url?: string;
}

interface PrintPreviewModalProps {
  show: boolean;
  onClose: () => void;
  formData: DocumentFormState;
  items: DocumentLineItem[];
  config: DocumentConfig;
  printTemplate: PrintPreviewTemplate | null;
  loadingTemplate: boolean;
  printPreviewTemplateLabel: string;
  printPreviewDownloadDocumentId: string | number | undefined;
  previewVisibleColumnCount: number;
  currencySymbol: string;
  formatCurrency: (value: number | string | undefined | null) => string;
  isDarkMode: boolean;
  isPurchaseDocument: boolean;
  clientBranding: ClientBranding | null;
  clientDetails: ClientDetails | null;
  selectedContact: Contact | null;
}

export function PrintPreviewModal({
  show,
  onClose,
  formData,
  items,
  config,
  printTemplate,
  loadingTemplate,
  printPreviewTemplateLabel,
  printPreviewDownloadDocumentId,
  previewVisibleColumnCount,
  currencySymbol,
  formatCurrency,
  isDarkMode,
  isPurchaseDocument,
  clientBranding,
  clientDetails,
  selectedContact,
}: PrintPreviewModalProps) {
  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-[140] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden flex flex-col"
        onClick={(event) => event.stopPropagation()}
      >
        <div
          className="px-6 py-4 flex items-center justify-between flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #0d2137 100%)' }}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
              <Printer className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Print Preview</h2>
              <p className="text-white/60 text-sm flex items-center gap-2 flex-wrap">
                <span>{formData.doc_number || `${config.labels.title} Draft`}</span>
                {loadingTemplate ? (
                  <span className="inline-flex items-center gap-1 text-white/50 text-xs">
                    <span className="animate-spin inline-block rounded-full h-3 w-3 border border-white/30 border-t-white" />
                    Loading template…
                  </span>
                ) : (
                  <>
                    <span className="text-white/50 text-xs">Template: {printPreviewTemplateLabel}</span>
                    <span className="text-white/40 text-xs">•</span>
                    <span className="text-white/50 text-xs">Live draft preview</span>
                  </>
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-white/10 rounded-xl">
              <span className="text-[11px] uppercase tracking-wide text-white/60">Template</span>
              <span className="text-sm text-white">{loadingTemplate ? 'Loading…' : printPreviewTemplateLabel}</span>
            </div>
            <button
              onClick={() => {
                const printContent = window.document.getElementById('print-preview-content');
                if (printContent) {
                  const tpl = printTemplate;
                  const fontFamily = tpl?.styles?.fontFamily || 'Arial, sans-serif';
                  const fontSize = tpl?.styles?.fontSize || 12;
                  const lineHeight = tpl?.styles?.lineHeight || 1.4;
                  const accentColor = tpl?.styles?.primaryColor || '#3b82f6';
                  const textColor = tpl?.styles?.textColor || '#1f2937';
                  const tableBorderColor = tpl?.styles?.tableBorderColor || '#e5e7eb';
                  const tableHeaderBgColor = tpl?.styles?.tableHeaderBgColor || '#f3f4f6';
                  const margins = tpl?.layout?.margins || { top: 20, right: 20, bottom: 20, left: 20 };

                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>${config.labels.title} - ${formData.doc_number || 'Document'}</title>
                        <style>
                          * { margin: 0; padding: 0; box-sizing: border-box; }
                          body { font-family: ${fontFamily}; font-size: ${fontSize}px; line-height: ${lineHeight}; padding: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm; color: ${textColor}; }
                          .header { display: flex; justify-content: space-between; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid ${accentColor}; }
                          .company-info h1 { font-size: 24px; color: ${textColor}; margin-bottom: 5px; }
                          .company-info p { color: #6b7280; font-size: 11px; }
                          .doc-info { text-align: right; }
                          .doc-info .doc-type { font-size: 28px; font-weight: bold; color: ${accentColor}; text-transform: uppercase; }
                          .doc-info .doc-number { font-size: 14px; color: #6b7280; margin-top: 5px; }
                          .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 30px; }
                          .detail-section h3 { font-size: 12px; font-weight: bold; color: #6b7280; text-transform: uppercase; margin-bottom: 10px; }
                          .detail-section p { color: ${textColor}; margin-bottom: 5px; }
                          table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
                          thead { background: ${tableHeaderBgColor}; }
                          th { padding: 12px 8px; text-align: left; font-size: 11px; font-weight: bold; color: #6b7280; text-transform: uppercase; border-bottom: 2px solid ${tableBorderColor}; }
                          th:last-child, td:last-child { text-align: right; }
                          td { padding: 12px 8px; border-bottom: 1px solid ${tableBorderColor}; }
                          .totals { margin-left: auto; width: 300px; }
                          .totals-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid ${tableBorderColor}; }
                          .totals-row.grand-total { font-size: 16px; font-weight: bold; color: ${textColor}; border-bottom: 2px solid ${accentColor}; border-top: 2px solid ${accentColor}; padding: 12px 0; }
                          .notes { margin-top: 30px; padding: 15px; background: #f9fafb; border-radius: 8px; }
                          .notes h4 { font-size: 11px; font-weight: bold; color: #6b7280; text-transform: uppercase; margin-bottom: 8px; }
                          .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid ${tableBorderColor}; text-align: center; color: #9ca3af; font-size: 10px; }
                          @media print { body { padding: ${margins.top}mm ${margins.right}mm ${margins.bottom}mm ${margins.left}mm; } @page { margin: 10mm; } }
                        </style>
                      </head>
                      <body>
                        ${printContent.innerHTML}
                      </body>
                      </html>
                    `);
                    printWindow.document.close();
                    printWindow.focus();
                    setTimeout(() => {
                      printWindow.print();
                      printWindow.close();
                    }, 250);
                  }
                }
              }}
              disabled={loadingTemplate}
              className="px-3 py-2 bg-white/10 hover:bg-white/20 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>
            <button
              onClick={async () => {
                if (!printPreviewDownloadDocumentId) {
                  toast.error('Please save the document first before downloading PDF');
                  return;
                }

                try {
                  toast.loading('Generating PDF...', { id: 'pdf-download' });
                  const token = authService.getTokens()?.access_token;
                  const templateParam = printTemplate?.id ? `?template_id=${printTemplate.id}&download=true` : '?download=true';
                  const response = await fetch(`/api/v1/documents/${printPreviewDownloadDocumentId}/pdf${templateParam}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });

                  if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || 'PDF generation failed');
                  }

                  const blob = await response.blob();
                  const url = URL.createObjectURL(blob);
                  const a = window.document.createElement('a');
                  a.href = url;
                  a.download = `${config.labels.title.toLowerCase().replace(/\s+/g, '-')}-${formData.doc_number || 'document'}.pdf`;
                  window.document.body.appendChild(a);
                  a.click();
                  window.document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success('PDF downloaded', { id: 'pdf-download' });
                } catch (err: unknown) {
                  console.error('PDF download error:', err);
                  toast.error(err instanceof Error ? err.message : 'Failed to download PDF', { id: 'pdf-download' });
                }
              }}
              disabled={!printPreviewDownloadDocumentId || loadingTemplate}
              title={!printPreviewDownloadDocumentId ? 'Save the document to enable PDF download' : 'Download PDF'}
              className="px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 text-sm"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/10 rounded-xl transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-800 p-4 sm:p-8">
          {loadingTemplate ? (
            <div className="flex items-center justify-center h-64 gap-3">
              <BrandedLoadingSpinner mode="compact" size="sm" showSurface={false} showProgressBar={false} />
              <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>Loading template…</span>
            </div>
          ) : (
            <div
              className="mx-auto"
              style={{ width: '210mm', maxWidth: '100%' }}
            >
              <div
                id="print-preview-content"
                className="bg-white shadow-xl mx-auto rounded-lg p-8"
                style={{
                  width: '210mm',
                  maxWidth: '100%',
                  minHeight: '297mm',
                  fontFamily: printTemplate?.styles?.fontFamily || 'Arial, sans-serif',
                  fontSize: `${printTemplate?.styles?.fontSize || 12}px`,
                  lineHeight: printTemplate?.styles?.lineHeight || 1.4,
                  color: printTemplate?.styles?.textColor || '#1f2937'
                }}
              >
                {/* Document Header */}
                {(printTemplate?.sections?.header?.show !== false) && (
                  <div className="header" style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '30px',
                    paddingBottom: '20px',
                    borderBottom: `2px solid ${printTemplate?.styles?.primaryColor || '#3b82f6'}`
                  }}>
                    <div className="company-info">
                      {printTemplate?.layout?.showLogo && clientBranding?.logo_url && (
                        <img
                          src={clientBranding.logo_url}
                          alt="Company Logo"
                          style={{
                            maxWidth: `${printTemplate?.layout?.logoSize?.width || 150}px`,
                            maxHeight: `${printTemplate?.layout?.logoSize?.height || 60}px`,
                            marginBottom: '10px'
                          }}
                        />
                      )}
                      <h1 style={{
                        fontSize: '24px',
                        color: printTemplate?.styles?.textColor || '#1f2937',
                        marginBottom: '5px'
                      }}>
                        {clientBranding?.trading_name || 'Your Company'}
                      </h1>
                      {printTemplate?.sections?.companyInfo?.show !== false && (clientDetails?.physical_address_line1 || clientDetails?.physical_address_city) && (
                        <p style={{ color: '#6b7280', fontSize: '11px' }}>
                          {[clientDetails?.physical_address_line1, clientDetails?.physical_address_line2, clientDetails?.physical_address_city, clientDetails?.physical_address_province, clientDetails?.physical_address_postal_code].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {printTemplate?.sections?.companyInfo?.show !== false && (clientDetails?.phone || clientDetails?.email) && (
                        <p style={{ color: '#6b7280', fontSize: '11px' }}>
                          {[clientDetails?.phone && `Tel: ${clientDetails.phone}`, clientDetails?.email && `Email: ${clientDetails.email}`].filter(Boolean).join(' | ')}
                        </p>
                      )}
                      {printTemplate?.sections?.companyInfo?.show !== false && clientDetails?.vat_number && (
                        <p style={{ color: '#6b7280', fontSize: '11px' }}>VAT: {clientDetails.vat_number}</p>
                      )}
                    </div>
                    <div className="doc-info" style={{ textAlign: 'right' }}>
                      <div className="doc-type" style={{
                        fontSize: '28px',
                        fontWeight: 'bold',
                        color: printTemplate?.styles?.primaryColor || '#3b82f6',
                        textTransform: 'uppercase'
                      }}>
                        {config.labels.title}
                      </div>
                      <div className="doc-number" style={{ fontSize: '14px', color: '#6b7280', marginTop: '5px' }}>
                        #{formData.doc_number}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '5px' }}>
                        Date: {formData.issued_at ? formatDateSAST(formData.issued_at) : '-'}
                      </div>
                    </div>
                  </div>
                )}

                {/* Bill To / Details */}
                {(printTemplate?.sections?.customerInfo?.show !== false) && (
                  <div className="details-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '32px' }}>
                    <div className="detail-section" style={{ padding: '18px 20px', border: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, borderRadius: '14px', background: '#f8fafc' }}>
                      <h3 style={{ fontSize: '12px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px' }}>
                        {isPurchaseDocument ? 'Supplier' : 'Bill To'}
                      </h3>
                      <p style={{ color: printTemplate?.styles?.textColor || '#1f2937', fontWeight: 'bold' }}>{formData.contact_name || '-'}</p>
                      {selectedContact?.email && <p style={{ color: '#6b7280', fontSize: '11px' }}>{selectedContact.email}</p>}
                      {selectedContact?.phone && <p style={{ color: '#6b7280', fontSize: '11px' }}>{selectedContact.phone}</p>}
                    </div>
                    <div className="detail-section" style={{ textAlign: 'right', padding: '18px 20px', border: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, borderRadius: '14px', background: '#f8fafc' }}>
                      <h3 style={{ fontSize: '12px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '10px' }}>
                        Details
                      </h3>
                      {formData.reference && <p style={{ color: printTemplate?.styles?.textColor || '#1f2937', marginBottom: '5px' }}>Ref: {formData.reference}</p>}
                      {formData.due_date && <p style={{ color: printTemplate?.styles?.textColor || '#1f2937', marginBottom: '5px' }}>Due: {formatDateSAST(formData.due_date)}</p>}
                      {formData.expiry_date && <p style={{ color: printTemplate?.styles?.textColor || '#1f2937', marginBottom: '5px' }}>Valid Until: {formatDateSAST(formData.expiry_date)}</p>}
                    </div>
                  </div>
                )}

                {/* Line Items Table */}
                {(printTemplate?.sections?.itemsTable?.show !== false) && (
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    marginBottom: '30px'
                  }}>
                    <thead style={{ background: printTemplate?.styles?.tableHeaderBgColor || '#f3f4f6' }}>
                      <tr>
                        <th style={{
                          padding: '12px 8px',
                          textAlign: 'left',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          borderBottom: `2px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`
                        }}>
                          {printTemplate?.table_columns?.description?.label || 'Description'}
                        </th>
                        {(printTemplate?.table_columns?.quantity?.show !== false) && (
                          <th style={{
                            padding: '12px 8px',
                            textAlign: 'center',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            borderBottom: `2px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                            width: '80px'
                          }}>
                            {printTemplate?.table_columns?.quantity?.label || 'Qty'}
                          </th>
                        )}
                        {(printTemplate?.table_columns?.unitPrice?.show !== false) && (
                          <th style={{
                            padding: '12px 8px',
                            textAlign: 'right',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            borderBottom: `2px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                            width: '100px'
                          }}>
                            {printTemplate?.table_columns?.unitPrice?.label || 'Unit Price'}
                          </th>
                        )}
                        {(printTemplate?.table_columns?.tax?.show !== false) && (
                          <th style={{
                            padding: '12px 8px',
                            textAlign: 'right',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            color: '#6b7280',
                            textTransform: 'uppercase',
                            borderBottom: `2px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                            width: '80px'
                          }}>
                            {printTemplate?.table_columns?.tax?.label || 'Tax'}
                          </th>
                        )}
                        <th style={{
                          padding: '12px 8px',
                          textAlign: 'right',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          color: '#6b7280',
                          textTransform: 'uppercase',
                          borderBottom: `2px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                          width: '120px'
                        }}>
                          {printTemplate?.table_columns?.total?.label || 'Total'}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.length > 0 ? items.map((item, idx) => {
                        const lineType = item.line_type || 'item';

                        if (lineType === 'header') {
                          return (
                            <tr key={idx}>
                              <td
                                colSpan={previewVisibleColumnCount}
                                style={{
                                  padding: '12px 10px',
                                  borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                                  background: '#f1f5f9',
                                  color: printTemplate?.styles?.textColor || '#1f2937',
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.08em',
                                  fontSize: '11px'
                                }}
                              >
                                {String(item.description || 'Section')}
                              </td>
                            </tr>
                          );
                        }

                        if (lineType === 'comment') {
                          return (
                            <tr key={idx}>
                              <td
                                colSpan={previewVisibleColumnCount}
                                style={{
                                  padding: '12px 10px',
                                  borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                                  background: '#fff7ed',
                                  color: '#7c2d12',
                                  fontStyle: 'italic',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word'
                                }}
                              >
                                {String(item.description || '')}
                              </td>
                            </tr>
                          );
                        }

                        if (lineType === 'spacer') {
                          return (
                            <tr key={idx}>
                              <td
                                colSpan={previewVisibleColumnCount}
                                style={{
                                  padding: '10px 8px',
                                  borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                                  background: '#ffffff'
                                }}
                              />
                            </tr>
                          );
                        }

                        if (lineType === 'subtotal') {
                          return (
                            <tr key={idx}>
                              <td
                                colSpan={Math.max(previewVisibleColumnCount - 1, 1)}
                                style={{
                                  padding: '12px 8px',
                                  borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                                  textAlign: 'right',
                                  background: '#eff6ff',
                                  color: printTemplate?.styles?.textColor || '#1f2937',
                                  fontWeight: 600
                                }}
                              >
                                {String(item.description || 'Subtotal')}
                              </td>
                              <td
                                style={{
                                  padding: '12px 8px',
                                  borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`,
                                  textAlign: 'right',
                                  background: '#eff6ff',
                                  fontFamily: 'monospace',
                                  fontWeight: 700,
                                  color: printTemplate?.styles?.primaryColor || '#2563eb'
                                }}
                              >
                                {currencySymbol} {formatCurrency(item.line_total || 0)}
                              </td>
                            </tr>
                          );
                        }

                        return (
                          <tr key={idx}>
                            <td style={{ padding: '12px 8px', borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}` }}>
                              <div style={{ fontWeight: 500 }}>{String(item.description || '-')}</div>
                              {item.item_code && <div style={{ fontSize: '10px', color: '#9ca3af' }}>{String(item.item_code)}</div>}
                            </td>
                            {(printTemplate?.table_columns?.quantity?.show !== false) && (
                              <td style={{ padding: '12px 8px', borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, textAlign: 'center' }}>{String(item.quantity || 0)}</td>
                            )}
                            {(printTemplate?.table_columns?.unitPrice?.show !== false) && (
                              <td style={{ padding: '12px 8px', borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, textAlign: 'right', fontFamily: 'monospace' }}>{currencySymbol} {formatCurrency(item.unit_price || 0)}</td>
                            )}
                            {(printTemplate?.table_columns?.tax?.show !== false) && (
                              <td style={{ padding: '12px 8px', borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, textAlign: 'right' }}>{String(item.tax_rate || 0)}%</td>
                            )}
                            <td style={{ padding: '12px 8px', borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, textAlign: 'right', fontFamily: 'monospace', fontWeight: 500 }}>{currencySymbol} {formatCurrency(item.line_total || 0)}</td>
                          </tr>
                        );
                      }) : (
                        <tr>
                          <td colSpan={5} style={{ padding: '30px', textAlign: 'center', color: '#9ca3af' }}>No items added</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}

                {/* Totals */}
                {(printTemplate?.sections?.totals?.show !== false) && (
                  <div className="totals" style={{ marginLeft: 'auto', width: '320px', border: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, borderRadius: '14px', padding: '16px 18px', background: '#fcfcfd' }}>
                    <div className="totals-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}` }}>
                      <span style={{ color: '#6b7280' }}>Subtotal</span>
                      <span style={{ fontFamily: 'monospace' }}>{currencySymbol} {formatCurrency(formData.subtotal || 0)}</span>
                    </div>
                    <div className="totals-row" style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}` }}>
                      <span style={{ color: '#6b7280' }}>VAT</span>
                      <span style={{ fontFamily: 'monospace' }}>{currencySymbol} {formatCurrency(formData.tax_total || 0)}</span>
                    </div>
                    <div className="totals-row grand-total" style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '12px 0',
                      fontSize: '16px',
                      fontWeight: 'bold',
                      borderBottom: `2px solid ${printTemplate?.styles?.primaryColor || '#3b82f6'}`,
                      borderTop: `2px solid ${printTemplate?.styles?.primaryColor || '#3b82f6'}`
                    }}>
                      <span>Total</span>
                      <span style={{ fontFamily: 'monospace' }}>{currencySymbol} {formatCurrency(formData.grand_total || 0)}</span>
                    </div>
                  </div>
                )}

                {/* Notes */}
                {(printTemplate?.sections?.notes?.show !== false) && formData.notes && (
                  <div className="notes" style={{ marginTop: '30px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
                    <h4 style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Notes</h4>
                    <p style={{ color: '#4b5563', whiteSpace: 'pre-wrap' }}>{formData.notes}</p>
                  </div>
                )}

                {/* Terms & Conditions */}
                {(printTemplate?.sections?.terms?.show !== false) && formData.terms_and_conditions && (
                  <div className="notes" style={{ marginTop: '15px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
                    <h4 style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Terms & Conditions</h4>
                    <p style={{ color: '#4b5563', whiteSpace: 'pre-wrap', fontSize: '10px' }}>{formData.terms_and_conditions}</p>
                  </div>
                )}

                {/* Bank Details */}
                {(printTemplate?.sections?.bankDetails?.show !== false) && printTemplate?.layout?.showPaymentDetails && clientDetails?.bank_name && (
                  <div className="notes" style={{ marginTop: '15px', padding: '15px', background: '#f9fafb', borderRadius: '8px' }}>
                    <h4 style={{ fontSize: '11px', fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', marginBottom: '8px' }}>Payment Details</h4>
                    <div style={{ color: '#4b5563', fontSize: '11px' }}>
                      <p><strong>Bank:</strong> {clientDetails.bank_name}</p>
                      {clientDetails.bank_account_number && <p><strong>Account:</strong> {clientDetails.bank_account_number}</p>}
                      {clientDetails.bank_branch_code && <p><strong>Branch Code:</strong> {clientDetails.bank_branch_code}</p>}
                      {clientDetails.bank_account_type && <p><strong>Account Type:</strong> {clientDetails.bank_account_type}</p>}
                      {clientDetails.trading_name && <p><strong>Reference:</strong> {formData.doc_number || 'Your Reference'}</p>}
                    </div>
                  </div>
                )}

                {/* Footer */}
                {(printTemplate?.sections?.footer?.show !== false) && (
                  <div className="footer" style={{ marginTop: '50px', paddingTop: '20px', borderTop: `1px solid ${printTemplate?.styles?.tableBorderColor || '#e5e7eb'}`, textAlign: 'center', color: '#9ca3af', fontSize: '10px' }}>
                    <p>{printTemplate?.custom_content?.footerText || 'Thank you for your business'}</p>
                    {clientBranding?.trading_name && <p style={{ marginTop: '5px' }}>{clientBranding.trading_name}</p>}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
